import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Timer, Flag, TrendingDown, ChevronRight } from "lucide-react";
import api from "../lib/apiClient";
import { PrimaryBtn, GhostBtn, Card, Loading, levelColor } from "../components/kit";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [gen, setGen] = useState(false);

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (data && !data.profile) navigate("/onboarding");
  }, [data, navigate]);

  const generate = async () => {
    setGen(true);
    toast.loading("Analyzing performance & building your plan…", { id: "g" });
    try { await api.post("/plan/generate"); toast.success("Plan ready!", { id: "g" }); await load(); }
    catch { toast.error("Generation failed", { id: "g" }); }
    finally { setGen(false); }
  };

  if (!data) return <div className="p-6"><Loading /></div>;
  if (!data.profile) return <div className="p-6"><Loading text="REDIRECTING…" /></div>;

  const { tournament, days_remaining, priorities, today, has_plan } = data;
  const todayDate = today?.date;
  const session = today?.session || {};
  const total = (session.golf || 0) + (session.mobility || 0) + (session.strength || 0);

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto">
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#A3A3A3] mb-1">Welcome back</div>
      <h1 className="font-heading text-3xl sm:text-4xl uppercase mb-8">{data.profile.player_name}</h1>

      {!tournament ? (
        <Card className="p-8 text-center">
          <p className="text-[#A3A3A3] mb-4">No upcoming tournament set.</p>
          <PrimaryBtn onClick={() => navigate("/profile")} data-testid="set-tournament">Set a tournament</PrimaryBtn>
        </Card>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {/* Countdown */}
          <Card className="p-6 md:col-span-2 flex flex-col justify-between relative overflow-hidden">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#CCFF00] mb-2 flex items-center gap-2"><Flag size={14} /> Next Tournament</div>
              <div className="font-heading text-2xl sm:text-3xl uppercase leading-none">{tournament.name}</div>
              <div className="text-sm text-[#A3A3A3] mt-2">
                {new Date(tournament.tournament_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
            </div>
            <div className="mt-6 flex items-end gap-3">
              <span className="font-heading text-6xl sm:text-7xl text-[#CCFF00] leading-none">{Math.max(0, days_remaining ?? 0)}</span>
              <span className="text-lg font-bold uppercase tracking-wide mb-2">Days<br />Remaining</span>
            </div>
          </Card>

          {/* Handicap */}
          <Card className="p-6 flex flex-col justify-center">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#A3A3A3] mb-2 flex items-center gap-2"><TrendingDown size={14} /> Current Handicap</div>
            <div className="font-heading text-6xl leading-none">{data.profile.handicap}</div>
            <div className="text-sm text-[#A3A3A3] mt-3">Avg score <span className="text-white font-bold">{data.profile.avg_score ?? "—"}</span></div>
            <div className="text-sm text-[#A3A3A3]">Best <span className="text-white font-bold">{data.profile.lowest_score ?? "—"}</span></div>
          </Card>
        </div>
      )}

      {/* Plan / Today */}
      {tournament && !has_plan && (
        <Card className="p-8 mt-4 text-center">
          <p className="text-[#A3A3A3] mb-4">Your AI training plan hasn't been generated yet.</p>
          <PrimaryBtn onClick={generate} disabled={gen} data-testid="generate-plan">{gen ? "Building…" : "Generate My Plan"}</PrimaryBtn>
        </Card>
      )}

      {has_plan && today && (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Card className="p-6 md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#CCFF00] flex items-center gap-2"><Timer size={14} /> Today · {today.label}</div>
              <span className={`text-[10px] font-bold uppercase tracking-wide border px-2 py-1 ${levelColor("MEDIUM")}`}>{today.phase}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-heading text-5xl text-[#CCFF00] leading-none">{total}</span>
              <span className="text-sm font-bold uppercase text-[#A3A3A3]">min total</span>
            </div>
            <div className="grid grid-cols-3 gap-3 my-5">
              {[["Golf", session.golf], ["Mobility", session.mobility], ["Strength", session.strength]].map(([k, v]) => (
                <div key={k} className="border border-[#262626] p-3 text-center">
                  <div className="font-heading text-2xl">{v || 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[#A3A3A3]">{k} min</div>
                </div>
              ))}
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-1">Primary Objective</div>
            <p className="text-base font-semibold mb-6">{today.objective}</p>
            <PrimaryBtn data-testid="start-training" onClick={() => navigate(`/training/${todayDate}`)} className="w-full flex items-center justify-center gap-2">
              Start Training <ChevronRight size={18} />
            </PrimaryBtn>
          </Card>

          {/* Priorities */}
          <Card className="p-6">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#A3A3A3] mb-4">Top 3 Priorities</div>
            <div className="space-y-3">
              {(priorities || []).slice(0, 3).map((pr, i) => (
                <div key={i} className="border border-[#262626] p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-heading text-lg uppercase">{i + 1}. {pr.skill}</span>
                    <span className={`text-[10px] font-bold uppercase border px-2 py-0.5 ${levelColor(pr.level)}`}>{pr.level}</span>
                  </div>
                  <div className="text-[11px] text-[#A3A3A3]">{pr.time_pct}% of practice time</div>
                </div>
              ))}
            </div>
            <GhostBtn className="w-full mt-4" onClick={() => navigate("/calendar")} data-testid="view-calendar">View full calendar</GhostBtn>
          </Card>
        </div>
      )}
    </div>
  );
}
